import { DialogComponent } from '@theme/dialog';
import { ThemeEvents } from '@theme/events';
import { applyLowStockBadges } from '@theme/storefront-api';
import { fetchConfig } from '@theme/utilities';
/**
 * A custom element that manages pickup availability display and drawer.
 * Updates pickup information when product variants change.
 * Allows selecting a pickup location and setting cart attributes.
 *
 * @extends {DialogComponent}
 */
class PickupAvailabilityComponent extends DialogComponent {
  /** @type {Record<string, string>} */
  #locationMap = {};

  /** @type {string | null} */
  #selectedLocationName = null;

  connectedCallback() {
    super.connectedCallback();

    this.productUrl = this.getAttribute('data-product-url');
    this.sectionId = this.getAttribute('data-section-id');

    this.#locationMap = JSON.parse(
      this.querySelector('[data-location-map]')?.textContent || '{}'
    );

    this.#loadExistingSelection();

    document.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantChange);

    const variantId = this.getAttribute('data-variant-id');
    if (variantId && !this.hasAttribute('hidden')) {
      applyLowStockBadges(this, variantId);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.variantUpdate, this.#handleVariantChange);
  }

  /**
   * Shows the pickup availability drawer.
   */
  showDrawer() {
    this.#highlightSelectedLocation(this.#selectedLocationName);
    this.showDialog();
  }

  /**
   * Closes the pickup availability drawer.
   */
  closeDrawer() {
    this.closeDialog();
  }

  /**
   * Handles selecting a pickup location and setting cart attributes.
   * @param {Event} event
   */
  async selectLocation(event) {
    const button = event.target?.closest('[data-location-name]');
    if (!button) return;

    const locationName = button.getAttribute('data-location-name');
    const storeId = this.#findStoreId(locationName);

    if (!storeId) {
      console.warn('No store ID found for location:', locationName);
      return;
    }

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({
          attributes: {
            pickup_store_name: locationName,
            pickup_store_id: storeId,
          },
        }),
      });

      await fetch(Theme.routes.cart_update_url, config);

      this.#selectedLocationName = locationName;
      this.#updateSelectedUI(locationName);
      this.#highlightSelectedLocation(locationName);
      this.closeDrawer();
    } catch (error) {
      console.error('Error setting pickup store:', error);
    }
  }

  /**
   * Loads existing pickup selection from cart attributes.
   */
  async #loadExistingSelection() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      const storeName = cart.attributes?.pickup_store_name;

      if (storeName) {
        this.#selectedLocationName = storeName;
        this.#updateSelectedUI(storeName);
      }
    } catch {
      // Selection state is non-critical
    }
  }

  /**
   * Finds the store ID for a location name, with normalized fallback.
   * @param {string} locationName
   * @returns {string | undefined}
   */
  #findStoreId(locationName) {
    let storeId = this.#locationMap[locationName];

    if (!storeId) {
      const normalize = (str) =>
        str.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase();
      const normalized = normalize(locationName);

      for (const [name, id] of Object.entries(this.#locationMap)) {
        if (normalize(name) === normalized) {
          storeId = id;
          break;
        }
      }
    }

    return storeId;
  }

  /**
   * Updates the selected-state banner visibility and text.
   * @param {string} locationName
   */
  #updateSelectedUI(locationName) {
    const selectedBanner = this.querySelector('.pickup-availability__selected');
    const selectedName = this.querySelector('.pickup-availability__selected-name');

    if (selectedBanner && selectedName) {
      selectedName.textContent = locationName;
      selectedBanner.removeAttribute('hidden');
    }
  }

  /**
   * Adds --active class to the selected location row in the drawer.
   * @param {string | null} locationName
   */
  #highlightSelectedLocation(locationName) {
    const locations = this.querySelectorAll('.pickup-availability__location');

    locations.forEach((location) => {
      const button = location.querySelector('[data-location-name]');

      if (locationName && button?.getAttribute('data-location-name') === locationName) {
        location.classList.add('--active');
      } else {
        location.classList.remove('--active');
      }
    });
  }

  /**
   * Clears the pickup selection from both UI and cart attributes.
   */
  async #clearSelection() {
    this.#selectedLocationName = null;

    const selectedBanner = this.querySelector('.pickup-availability__selected');
    if (selectedBanner) {
      selectedBanner.setAttribute('hidden', '');
    }

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({
          attributes: {
            pickup_store_name: '',
            pickup_store_id: '',
          },
        }),
      });

      await fetch(Theme.routes.cart_update_url, config);
    } catch {
      // Non-critical
    }
  }

  /**
   * Handles variant update events and updates pickup availability.
   * @param {import('@theme/events').VariantUpdateEvent} event - The variant update event
   */
  #handleVariantChange = async (event) => {
    const variant = event.detail?.resource;
    const variantId = variant?.id;

    if (!variantId) {
      this.setAttribute('hidden', '');
      this.closeDrawer();
      return;
    }

    try {
      await this.#fetchPickupAvailability(variantId);
    } catch (error) {
      console.error('Error fetching pickup availability:', error);
    }
  };

  /**
   * Fetches pickup availability for a specific variant.
   * After content replacement, re-applies selected state or clears if unavailable.
   * @param {number} variantId - The variant ID
   */
  async #fetchPickupAvailability(variantId) {
    if (!this.productUrl || !this.sectionId) return;

    const url = `${this.productUrl}?variant=${variantId}&section_id=${this.sectionId}`;

    try {
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      const newPickupAvailability = doc.querySelector('pickup-availability-component');

      if (newPickupAvailability) {
        const isHidden = newPickupAvailability.hasAttribute('hidden');

        if (isHidden) {
          this.setAttribute('hidden', '');
        } else {
          this.removeAttribute('hidden');

          const newSummary = newPickupAvailability.querySelector('.pickup-availability__summary');
          const newDrawerContent = newPickupAvailability.querySelector('.pickup-availability__drawer-content');
          const newDrawerTitle = newPickupAvailability.querySelector('.pickup-availability__drawer-title');

          const currentSummary = this.querySelector('.pickup-availability__summary');
          const currentDrawerContent = this.querySelector('.pickup-availability__drawer-content');
          const currentDrawerTitle = this.querySelector('.pickup-availability__drawer-title');

          if (newSummary && currentSummary) {
            currentSummary.innerHTML = newSummary.innerHTML;
          }

          if (newDrawerContent && currentDrawerContent) {
            currentDrawerContent.innerHTML = newDrawerContent.innerHTML;
          }

          if (newDrawerTitle && currentDrawerTitle) {
            currentDrawerTitle.innerHTML = newDrawerTitle.innerHTML;
          }

          this.setAttribute('data-variant-id', variantId);

           // Re-apply or clear selected state after content replacement
          if (this.#selectedLocationName) {
            const stillAvailable = this.querySelector(
              `.pickup-availability__select-button[data-location-name="${CSS.escape(this.#selectedLocationName)}"]`
            );

            if (stillAvailable) {
              this.#highlightSelectedLocation(this.#selectedLocationName);
            } else {
              this.#clearSelection();
            }
          }

          await applyLowStockBadges(this, variantId);
        }
      } else {
        this.setAttribute('hidden', '');
      }
    } catch (error) {
      console.error('Failed to fetch pickup availability:', error);
      this.setAttribute('hidden', '');
    }
  }
}

if (!customElements.get('pickup-availability-component')) {
  customElements.define('pickup-availability-component', PickupAvailabilityComponent);
}
